'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from "@/contexts/AuthContext"
import {
  DndContext,
  closestCenter,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react'; // Using lucide-react for the handle icon

// Import the new card components
import OverallSummaryCard from '@/components/budgets/overview/OverallSummaryCard'
import ActivityChartCard from '@/components/budgets/overview/ActivityChartCard'
import BudgetCategoriesCard from '@/components/budgets/overview/BudgetCategoriesCard'
import TransactionsTableCard from '@/components/budgets/overview/TransactionsTableCard'

// Data structure interfaces
interface BudgetItem {
  id: string;
  userId: string;
  description: string;
  merchantName: string;
  amount: number;
  date: string;
}

interface BudgetCategory {
  id: string;
  name: string;
  icon: string; 
  allocated: number;
  spent: number;
  items?: BudgetItem[];
  color?: string;
}

// Combined type for aggregated transactions
interface AggregatedBudgetItem extends BudgetItem {
  categoryName: string;
  categoryIcon: string;
}

// Define Collaborator type here for use in state and props
interface Collaborator {
  id: string;
  name: string;
  initials: string;
  imageUrl: string | null;
}

// Define ChartDataPoint type used in chartData calculation
interface ChartDataPoint {
  date: string; // Formatted date for display (e.g., "Jul 1")
  amount: number;
  fullDate: string; // Original YYYY-MM-DD date
}

// Mock Data
const budgetCategoriesData: BudgetCategory[] = [
  {
    id: 'housing',
    name: 'Housing',
    icon: '🏠',
    allocated: 1500,
    spent: 1450.75,
    color: '#2563eb',
    items: [
      { id: 'h1', userId: 'user1', description: 'Rent Payment', merchantName: 'Landlord Property Mgmt', amount: 1400, date: '2024-07-01' },
      { id: 'h2', userId: 'user2', description: 'Minor Repair', merchantName: 'Local Hardware', amount: 50.75, date: '2024-07-15' },
    ],
  },
  {
    id: 'utilities',
    name: 'Utilities',
    icon: '💡',
    allocated: 250,
    spent: 215.50,
    color: '#f59e0b',
    items: [
      { id: 'u1', userId: 'user1', description: 'Electricity Bill', merchantName: 'City Power', amount: 120.00, date: '2024-07-10' },
      { id: 'u2', userId: 'user1', description: 'Water Bill', merchantName: 'City Water Dept', amount: 45.50, date: '2024-07-12' },
      { id: 'u3', userId: 'user2', description: 'Internet Bill', merchantName: 'FastNet ISP', amount: 50.00, date: '2024-07-05' },
    ],
  },
  {
    id: 'groceries',
    name: 'Groceries',
    icon: '🛒',
    allocated: 400,
    spent: 375.20,
    color: '#10b981',
    items: [
       { id: 'g1', userId: 'user2', description: 'SuperMart Run', merchantName: 'SuperMart', amount: 150.10, date: '2024-07-03' },
       { id: 'g2', userId: 'user1', description: 'Local Market Trip', merchantName: 'Local Market', amount: 85.00, date: '2024-07-11' },
       { id: 'g3', userId: 'user2', description: 'Quick Groceries', merchantName: 'SuperMart', amount: 140.10, date: '2024-07-18' },
    ]
  },
  {
    id: 'transport',
    name: 'Transportation',
    icon: '🚗',
    allocated: 150,
    spent: 165.00, // Over budget example
    color: '#8b5cf6',
     items: [
       { id: 't1', userId: 'user1', description: 'Gas Fill-up', merchantName: 'Gas Station Plus', amount: 60.00, date: '2024-07-02' },
       { id: 't2', userId: 'user2', description: 'Bus Fare', merchantName: 'City Transit', amount: 25.00, date: '2024-07-09' },
       { id: 't3', userId: 'user1', description: 'Gas Fill-up', merchantName: 'Speedy Gas', amount: 80.00, date: '2024-07-16' },
    ]
  },
];

// Initial Mock Collaborator Data (will be put into state)
const initialCollaborators: Collaborator[] = [
  { id: 'user1', name: 'User', initials: 'U', imageUrl: null }, // Default placeholder for dynamic user
  { id: 'user2', name: 'Jane Doe', initials: 'JD', imageUrl: 'https://i.pravatar.cc/150?u=user2alt' }, 
];

// Helper to get Initials (Keep here, might be needed for currentUser)
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .substring(0, 2)
}

// Helper to format currency (Keep here, used in multiple calculations before passing to children)
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// Helper to format date (Keep here, used in transaction processing before passing)
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Helper to format date (short for axis/tooltip)
const formatShortDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Define IDs for the sortable sections
const CARD_IDS = {
  SUMMARY: 'overallSummary',
  ACTIVITY_CATEGORIES: 'activityAndCategories',
  TRANSACTIONS: 'transactionsTable',
};

// Sortable Item Wrapper Component
function SortableItem({ id, children }: { id: string; children: React.ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group w-full">
      {/* Drag Handle: Positioned top-left */}
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag handle"
        className="absolute top-2 left-2 p-1 bg-gray-200 dark:bg-gray-700 rounded text-gray-500 dark:text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-grab focus:cursor-grabbing z-20"
      >
        <GripVertical size={16} />
      </button>
      {/* Make content non-interactive during drag */}
      <div style={{ pointerEvents: isDragging ? 'none' : undefined }}>
        {children}
      </div>
    </div>
  );
}

export default function BudgetsOverviewPage() {
  const { currentUser } = useAuth(); // Get current user
  const [collaboratorData, setCollaboratorData] = useState<Collaborator[]>(initialCollaborators); // Use Collaborator type
  const [transactionSearchQuery, setTransactionSearchQuery] = useState(""); // State for transaction search
  // State to manage the order of the cards
  const [cardOrder, setCardOrder] = useState<string[]>([
    CARD_IDS.SUMMARY,
    CARD_IDS.ACTIVITY_CATEGORIES,
    CARD_IDS.TRANSACTIONS,
  ]);

  // Sensors for dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Effect to update user1 collaborator data based on currentUser
  useEffect(() => {
    if (currentUser) {
      setCollaboratorData(prevCollabs => 
        prevCollabs.map(collab => {
          if (collab.id === 'user1') {
            const displayName = currentUser.displayName || "User";
            return {
              ...collab,
              name: displayName,
              initials: getInitials(displayName),
              imageUrl: "https://images.steamusercontent.com/ugc/937185524768962464/98DD1D8BB7235A25054111C9436A9E11BF47F356/?imw=512&&ima=fit&impolicy=Letterbox&imcolor=%23000000&letterbox=false",
            };
          }
          return collab;
        })
      );
    }
  }, [currentUser]);

  // Calculate overall totals
  const totalAllocated = useMemo(() => budgetCategoriesData.reduce((sum, cat) => sum + cat.allocated, 0), [budgetCategoriesData]);
  const totalSpent = useMemo(() => budgetCategoriesData.reduce((sum, cat) => sum + cat.spent, 0), [budgetCategoriesData]);
  const overallProgress = useMemo(() => (totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0), [totalAllocated, totalSpent]);
  const remainingBudget = useMemo(() => totalAllocated - totalSpent, [totalAllocated, totalSpent]);

  // Calculate days remaining (Keep logic here)
  const daysRemaining = useMemo(() => {
    const today = new Date();
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const timeDiff = endOfMonth.getTime() - today.getTime();
    return Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
  }, []);

  // Aggregate and sort all transactions
  const allTransactions: AggregatedBudgetItem[] = useMemo(() => 
    budgetCategoriesData
      .flatMap(category => 
        category.items?.map(item => ({ 
          ...item, 
          categoryName: category.name, 
          categoryIcon: category.icon 
        })) ?? []
      )
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [budgetCategoriesData]
  );

  // Process data for Bar Chart (Daily Spending)
  const chartData: ChartDataPoint[] = useMemo(() => {
    const dailySpending = allTransactions.reduce((acc, item) => {
      const date = item.date; // Use YYYY-MM-DD string as key
      acc[date] = (acc[date] || 0) + item.amount;
      return acc;
    }, {} as { [key: string]: number });

    return Object.entries(dailySpending)
      .map(([date, amount]) => ({ 
        date: formatShortDate(date), // Format date for display
        amount: amount,
        fullDate: date // Keep original date for sorting if needed later
      }))
      .sort((a, b) => new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime());
  }, [allTransactions]);

  // Filter transactions based on search query
  const filteredTransactions = useMemo(() => {
    if (!transactionSearchQuery) {
      return allTransactions;
    }
    const lowerCaseQuery = transactionSearchQuery.toLowerCase();
    return allTransactions.filter(item => 
      item.merchantName.toLowerCase().includes(lowerCaseQuery) ||
      item.description.toLowerCase().includes(lowerCaseQuery) ||
      item.categoryName.toLowerCase().includes(lowerCaseQuery) ||
      formatCurrency(item.amount).includes(lowerCaseQuery) || // Search formatted currency
      formatDate(item.date).toLowerCase().includes(lowerCaseQuery) // Search formatted date
    );
  }, [allTransactions, transactionSearchQuery]);

  // Placeholder Handlers
  const handleSettingsClick = () => console.log('Settings clicked');
  const handleAddCollaboratorClick = () => console.log('Add collaborator clicked');
  const handleResetPeriodClick = () => console.log('Reset period clicked');
  const handleAddCategoryClick = () => console.log('Add category clicked');
  const handleFilterClick = () => console.log('Filter clicked');
  const handleViewTransaction = (id: string) => console.log('View transaction clicked', id);
  const handleDeleteTransaction = (id: string) => console.log('Delete transaction clicked', id);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTransactionSearchQuery(event.target.value);
  };

  // Drag End Handler
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCardOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  // Memoize card components to avoid unnecessary re-renders
  const cardComponents = useMemo(() => ({
    [CARD_IDS.SUMMARY]: (
      <OverallSummaryCard
        overallProgress={overallProgress}
        totalSpent={totalSpent}
        totalAllocated={totalAllocated}
        remainingBudget={remainingBudget}
        collaboratorData={collaboratorData}
        daysRemaining={daysRemaining}
        onSettingsClick={handleSettingsClick}
        onAddCollaboratorClick={handleAddCollaboratorClick}
        onResetPeriodClick={handleResetPeriodClick}
      />
    ),
    [CARD_IDS.ACTIVITY_CATEGORIES]: (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActivityChartCard chartData={chartData} />
        <BudgetCategoriesCard
          budgetCategoriesData={budgetCategoriesData}
          onSettingsClick={handleSettingsClick}
          onAddCategoryClick={handleAddCategoryClick}
        />
      </div>
    ),
    [CARD_IDS.TRANSACTIONS]: (
      <TransactionsTableCard
        transactions={filteredTransactions}
        collaboratorData={collaboratorData}
        searchQuery={transactionSearchQuery}
        onSearchChange={handleSearchChange}
        onFilterClick={handleFilterClick}
        onViewTransaction={handleViewTransaction}
        onDeleteTransaction={handleDeleteTransaction}
      />
    ),
  }), [
    overallProgress, totalSpent, totalAllocated, remainingBudget, collaboratorData, daysRemaining,
    chartData, budgetCategoriesData, filteredTransactions, transactionSearchQuery // Dependencies for memoization
  ]);

  return (
    <div className="flex-1 overflow-hidden p-4 md:p-6">
      <h1 className="text-lg font-semibold md:text-2xl mb-4">Needs Budget Overview</h1>

      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={cardOrder}
          strategy={rectSortingStrategy}
        >
          <div className="flex flex-wrap gap-6">
            {cardOrder.map((id) => (
              <SortableItem key={id} id={id}>
                {cardComponents[id]}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
} 